import { useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Button, Card, List, Space, Tag, Typography, message } from 'antd';
import { AppstoreAddOutlined } from '@ant-design/icons';
import { useApi } from '../../hooks/use_api';
import { useNodeApps } from '../../hooks/use_node_apps';
import { useNodeInfo } from '../../hooks/use_node_info';
import { useSuperAdmins } from '../../hooks/use_super_admins';

const { Title, Text, Paragraph } = Typography;

/**
 * Apps (/apps): what this node offers — its catalog (GET /api/apps/catalog),
 * each app with what it may do — and, under each, where it is already
 * installed in the node itself (use_node_apps): those anyone signed in here
 * may open. Reached from the sidebar's ⋯ menu, beside Account.
 *
 * A superadmin installs an app in the node from here, the same request the
 * node settings' Apps tab makes (POST /api/apps/node). An app for one
 * person, context or relationship is installed from that entity's page.
 */
function AppsPage() {
    const api = useApi();
    const navigate = useNavigate();
    const { apps: nodeApps, ready: nodeAppsReady, refresh } = useNodeApps();
    const { nodeName } = useNodeInfo();
    const { isServerAdmin } = useSuperAdmins();
    const [catalog, setCatalog] = useState(null); // null until fetched
    const [failed, setFailed] = useState(false);
    const [busy, setBusy] = useState(null);

    useEffect(() => {
        let alive = true;
        api.get('/api/apps/catalog')
            .then(rows => { if (alive) setCatalog(Array.isArray(rows) ? rows : []); })
            .catch(() => { if (alive) { setCatalog([]); setFailed(true); } });
        return () => { alive = false; };
    }, [api]);

    const install = kind => {
        setBusy(kind);
        api.post('/api/apps/node', { kind })
            .then(inst => {
                message.success(`Installed in ${nodeName || 'this node'}`);
                // The sidebar lists node apps for everyone, so refresh that too.
                return refresh().then(() => navigate(`/app/${inst.id}`));
            })
            .catch(err => message.error(err.status === 403
                ? 'Only a superadmin can install apps in the node'
                : (err.message || 'Could not install')))
            .finally(() => setBusy(null));
    };

    const installedOf = kind => nodeApps.filter(inst => inst.kind === kind);

    return (
        <div style={{ margin: 20, maxWidth: 720 }}>
            <Card loading={catalog === null || !nodeAppsReady}>
                <Title level={4} style={{ marginTop: 0 }}>Apps</Title>
                <Paragraph type="secondary">
                    What this node offers. An app installed in the node is for everyone signed
                    in here and sits in the sidebar; an app for one person, context or
                    relationship is installed from that entity's page.
                </Paragraph>
                <List
                    dataSource={catalog ?? []}
                    locale={{ emptyText: failed ? "Couldn't load the apps. Try refreshing." : 'This node offers no apps yet' }}
                    renderItem={m => {
                        const installed = installedOf(m.kind);
                        return (
                            <List.Item
                                actions={isServerAdmin ? [
                                    <Button
                                        key="install"
                                        type="link"
                                        icon={<AppstoreAddOutlined />}
                                        loading={busy === m.kind}
                                        disabled={!!busy && busy !== m.kind}
                                        onClick={() => install(m.kind)}
                                        style={{ padding: 0 }}
                                    >
                                        Install in node
                                    </Button>,
                                ] : undefined}
                            >
                                <List.Item.Meta
                                    title={<Space size={6}>{m.name}<Tag>{m.kind}</Tag></Space>}
                                    description={(
                                        <Space direction="vertical" size={2}>
                                            {m.description && <div>{m.description}</div>}
                                            <Text type="secondary" style={{ fontSize: 12 }}>
                                                Version {m.version} · it may: {(m.scopes || []).join(', ') || 'nothing beyond itself'}
                                            </Text>
                                            {installed.length > 0 && (
                                                <Text style={{ fontSize: 12 }}>
                                                    In this node:{' '}
                                                    {installed.map((inst, i) => (
                                                        <span key={inst.id}>
                                                            {i > 0 && ', '}
                                                            <Link to={`/app/${inst.id}`}>{inst.name}</Link>
                                                        </span>
                                                    ))}
                                                </Text>
                                            )}
                                        </Space>
                                    )}
                                />
                            </List.Item>
                        );
                    }}
                />
            </Card>
        </div>
    );
}

export default AppsPage;
